import Papa, { type ParseResult } from "papaparse";

import { PropertySchemaType } from "~/common/types";
import { type PropertySchemaColumn, PropertySchemaColumnsSchema } from "./propertySchemaCsvUploadValidation";

// types.ts
type UrlAllowItem = { value: string | null; regex: string | null };
type SelectOption = { value: string; label: { ja: string } };
type CreatePropertySchema = {
  position?: number;
  id: undefined;
  name: string;
  description: string;
  required: boolean;
  unique: boolean;
  propertyAttributeTypeId: undefined;
  propertySchema: {
    id: undefined;
    propertyTypeId: PropertySchemaType;
    selectOptions: SelectOption[];
    urlAllowList: UrlAllowItem[];
  };
};

const isFilled = (x: unknown): boolean => x !== null && x !== undefined && (typeof x !== "string" || x.trim() !== "");

/** URL_allow_list_N_{value|regex} -> [{ value, regex }, ...] */
function buildUrlAllowList(obj: Record<string, unknown>, opts: { includeEmpty?: boolean } = {}): UrlAllowItem[] {
  const includeEmpty = opts.includeEmpty ?? false;
  const map = new Map<number, UrlAllowItem>();

  for (const [k, v] of Object.entries(obj)) {
    const m = k.match(/^URL_allow_list_(\d+)_(value|regex)$/);
    if (!m) continue;
    const idx = Number(m[1]);
    const field = m[2] as "value" | "regex";
    if (!map.has(idx)) map.set(idx, { value: null, regex: null });

    const val = typeof v === "string" ? (v as string).trim() : ((v as string | null | undefined) ?? null);

    (map.get(idx) as UrlAllowItem)[field] = val;
  }

  const arr = [...map.entries()].sort((a, b) => a[0] - b[0]).map(([, rec]) => rec);

  return includeEmpty ? arr : arr.filter((p) => isFilled(p.value) || isFilled(p.regex));
}

/** select_option_N_{value|label} -> [{ value, label }, ...] (complete pairs only) */
function buildSelectOptions(obj: Record<string, unknown>): SelectOption[] {
  const map = new Map<number, { value?: string | null; label?: string | null }>();

  for (const [k, v] of Object.entries(obj)) {
    const m = k.match(/^select_option_(\d+)_(value|label)$/);
    if (!m) continue;
    const idx = Number(m[1]);
    const field = m[2] as "value" | "label";
    if (!map.has(idx)) map.set(idx, {});

    const val = typeof v === "string" ? (v as string).trim() : ((v as string | null | undefined) ?? null);

    (map.get(idx) as Record<string, unknown>)[field] = val;
  }

  return [...map.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, r]) => ({
      value: (r.value ?? "") as string,
      label: { ja: (r.label ?? "") as string },
    }))
    .filter((p) => isFilled(p.value) && isFilled(p.label.ja));
}

type CsvRow = Record<string, unknown>;

export function isCsvFile(file: File): boolean {
  const nameOk = file.name.toLowerCase().endsWith(".csv");
  const typeOk = file.type === "text/csv" || file.type === "application/vnd.ms-excel";
  return nameOk || typeOk;
}

function parseCsvRows(file: File): Promise<ParseResult<CsvRow>> {
  return new Promise((resolve, reject) => {
    Papa.parse<CsvRow>(file, {
      header: true, // treat first row as header
      dynamicTyping: true, // numbers become numbers, booleans become booleans
      skipEmptyLines: "greedy", // ignore blank lines
      transformHeader: (h) => h.trim(),
      complete: resolve,
      error: reject,
    });
  });
}

function validateColumns(rows: CsvRow[]): PropertySchemaColumn[] {
  const result = PropertySchemaColumnsSchema.safeParse(rows);
  if (!result.success) {
    throw new Error("CSV_VALIDATION_FAILED", { cause: result.error });
  }

  return result.data;
}

function toPropertySchemas(columns: PropertySchemaColumn[]): Array<{ position: number; schema: CreatePropertySchema }> {
  return columns.map((column) => {
    const schema: CreatePropertySchema = {
      id: undefined,
      position: column.position,
      name: column.name,
      description: column.description,
      required: column.required,
      unique: column.unique,
      propertyAttributeTypeId: undefined,
      propertySchema: {
        id: undefined,
        propertyTypeId: column.type,
        selectOptions: [],
        urlAllowList: [],
      },
    };

    if (column.type === PropertySchemaType.Url) {
      schema.propertySchema.urlAllowList = buildUrlAllowList(column);
    }

    if (column.type === PropertySchemaType.Select || column.type === PropertySchemaType.MultiSelect) {
      schema.propertySchema.selectOptions = buildSelectOptions(column);
    }

    return { position: column.position, schema };
  });
}

/** End-to-end convenience: File -> CreatePropertySchema[] (with positions) */
export async function csvFileToPropertySchemas(
  file: File,
): Promise<Array<{ position: number; schema: CreatePropertySchema }>> {
  const { data } = await parseCsvRows(file);
  const columns = validateColumns(data);
  return toPropertySchemas(columns);
}

/**
 * Orders parsed rows for insertion into the form.
 *
 * `position` conveys relative order only: the upload template does not pin down whether
 * authors number from 0 or 1, and gaps are allowed.
 */
export function orderPropertySchemasForInsert(items: Array<{ position: number; schema: CreatePropertySchema }>) {
  return [...items]
    .sort((a, b) => a.position - b.position)
    .map(({ schema }) => ({
      ...schema,
      propertySchema: {
        ...schema.propertySchema,
        // Entries with neither value nor regex are already dropped by buildUrlAllowList, so
        // a missing side is an unfilled form field rather than a row to discard.
        urlAllowList: schema.propertySchema.urlAllowList?.map((item) => ({
          value: item.value ?? "",
          regex: item.regex ?? "",
        })),
      },
    }));
}
